import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import { FaClock, FaEnvelope, FaMapMarkerAlt, FaPhoneAlt } from 'react-icons/fa';
import Reveal from './Reveal';

const contacts = [
  {
    name: 'Ben',
    phoneDisplay: '07969 087406',
    phoneHref: 'tel:+447969087406',
  },
  {
    name: 'Ian',
    phoneDisplay: '07854 826914',
    phoneHref: 'tel:+447854826914',
  },
];

const contactFormEndpoint =
  process.env.REACT_APP_CONTACT_FORM_ENDPOINT?.trim() || '';

const createInitialFormState = () => ({
  name: '',
  phone: '',
  email: '',
  postcode: '',
  details: '',
  website: '',
});

const createEmptyStatus = () => ({
  type: '',
  message: '',
});

const ContactSection = () => {
  const [formState, setFormState] = useState(createInitialFormState);
  const [formStartedAt, setFormStartedAt] = useState(() => Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(createEmptyStatus);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((previousState) => ({
      ...previousState,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!contactFormEndpoint) {
      setStatus({
        type: 'error',
        message:
          'The website form endpoint is not configured yet. Please call or email us directly for now.',
      });
      return;
    }

    setIsSubmitting(true);
    setStatus(createEmptyStatus());

    try {
      const response = await fetch(contactFormEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formState,
          startedAt: new Date(formStartedAt).toISOString(),
          submittedAt: new Date().toISOString(),
          sourceUrl: window.location.href,
        }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          result?.message ||
            'The form could not be sent right now. Please try again or call us directly.'
        );
      }

      setFormState(createInitialFormState());
      setFormStartedAt(Date.now());
      setStatus({
        type: 'success',
        message:
          result?.message ||
          'Thanks. Your quote request has been sent and Moonwood Electrical will get back to you shortly.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message:
          error.message === 'Failed to fetch'
            ? 'The website could not reach the contact service. Please try again or call us directly.'
            : error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="content-section contact-section" id="contact">
      <Container>
        <Reveal className="section-heading section-heading--split">
          <div>
            <p className="section-eyebrow">Contact</p>
            <h2>Call direct or send details for a quote.</h2>
          </div>
          <p className="section-heading__intro">
            Speak to Ben or Ian, email the team, or use the form to send the
            basics.
          </p>
        </Reveal>

        <div className="contact-layout">
          <Reveal as="aside" className="contact-card contact-card--summary">
            <h3>Direct contact</h3>
            <div className="contact-list">
              {contacts.map((person) => (
                <a className="contact-card__detail" href={person.phoneHref} key={person.name}>
                  <FaPhoneAlt aria-hidden="true" />
                  <span>{person.name}: {person.phoneDisplay}</span>
                </a>
              ))}
              <a
                className="contact-card__detail"
                href="mailto:moonwoodelectrical@gmail.com"
              >
                <FaEnvelope aria-hidden="true" />
                <span>moonwoodelectrical@gmail.com</span>
              </a>
            </div>

            <div className="contact-meta-list">
              <p className="contact-mini-meta">
                <FaClock aria-hidden="true" />
                <span>Monday to Friday, 7:30 AM to 5:00 PM</span>
              </p>
              <p className="contact-mini-meta">
                <FaMapMarkerAlt aria-hidden="true" />
                <span>Spixworth, Norwich and surrounding areas</span>
              </p>
            </div>
          </Reveal>

          <Reveal className="quote-card" delay={120}>
            <p className="section-eyebrow">Request a quote</p>
            <h3>Send the job details.</h3>
            <p>
              Share what needs doing, where it is and anything time-sensitive.
            </p>

            <form className="quote-form" onSubmit={handleSubmit}>
              <div className="quote-form__grid">
                <div className="field">
                  <label htmlFor="name">Name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formState.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="field">
                  <label htmlFor="phone">Phone</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formState.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formState.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="field">
                  <label htmlFor="postcode">Postcode</label>
                  <input
                    id="postcode"
                    name="postcode"
                    type="text"
                    value={formState.postcode}
                    onChange={handleChange}
                  />
                </div>

                <div className="field field--full">
                  <label htmlFor="details">What do you need help with?</label>
                  <textarea
                    id="details"
                    name="details"
                    rows="6"
                    value={formState.details}
                    onChange={handleChange}
                    placeholder="Describe the work, where it is, and anything time-sensitive."
                    required
                  />
                </div>

                <div className="field field--hidden" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formState.website}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <button
                className="site-button"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Start quote request'}
              </button>
            </form>

            <p className="quote-form__note">
              Prefer to talk first? Use the direct numbers beside the form.
            </p>

            {status.message ? (
              <p
                className={`quote-form__status quote-form__status--${status.type}`}
                role={status.type === 'error' ? 'alert' : 'status'}
              >
                {status.message}
              </p>
            ) : null}
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

export default ContactSection;
